import { Alert, AlertDescription, AlertIcon, Box, Button, Flex, Switch, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { ActionButton } from '../../../../components/ActionButton'
import FormActions from '../../../../components/ActionButton/FormActions'
import { EmptyState } from '../../../../components/EmptyState'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import { Icon } from '../../../../components/Icon'
import { LoadingBlock } from '../../../../components/Loading'
import { PaneHeader } from '../../../../components/PaneHeader'
import { ApiErrorShape, formatDateTime, useAppToast } from '../../../../helpers'
import { EventSurveyService } from '../../../../services/Event/EventSurveyService'
import { IEventSurveyConfig } from '../../../../services/Event/surveyTypes'
import { IEventSurveyDraft, toDraft, toPayload, validateDraft } from './eventSurveyForm'
import EventSurveyPreview from './EventSurveyPreview'
import EventSurveyQuestionsEditor from './EventSurveyQuestionsEditor'

interface IEventSurveyModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: number
  eventName: string
  eventSlug: string
  eventImage?: string | null
  /** Mostra o atalho para a página de resultados (permissão `view_event_survey`). */
  canViewResults?: boolean
  /** Avisa a página que a pesquisa passou a existir, para ela mostrar o botão de resultados. */
  onSaved?: () => void
}

/**
 * Configuração da pesquisa NPS do evento (ABRA-370): perguntas à esquerda, prévia da página do
 * site à direita. Sem pesquisa salva, as perguntas vêm do modelo padrão (o Google Forms que o
 * time usava), e salvar é o que cria a pesquisa.
 *
 * O link chega ao participante por e-mail, um por pessoa: no e-mail dos certificados
 * (#LINK_PESQUISA) ou no "Enviar pesquisa" — por isso não há "copiar link" aqui: não existe um
 * link da pesquisa, existe um por participante.
 */
export default function EventSurveyModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  eventSlug,
  eventImage,
  canViewResults = false,
  onSaved
}: IEventSurveyModalProps) {
  const service = useMemo(() => new EventSurveyService(), [])
  const toast = useAppToast()

  const [config, setConfig] = useState<IEventSurveyConfig | null>(null)
  const [draft, setDraft] = useState<IEventSurveyDraft | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})

  const load = () => {
    setLoading(true)
    setLoadError(false)
    setErrors({})
    service
      .getConfig(eventId)
      .then((result) => {
        setConfig(result)
        setDraft(toDraft(result))
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar a pesquisa NPS', error)
      })
      .finally(() => setLoading(false))
  }

  // Recarrega a cada abertura: outra pessoa pode ter salvo, e as travas das perguntas dependem
  // das respostas que chegaram desde a última vez.
  useEffect(() => {
    if (isOpen) load()
  }, [isOpen, eventId])

  const handleSave = () => {
    if (!draft) return

    const localErrors = validateDraft(draft)
    setErrors(localErrors)

    if (Object.keys(localErrors).length > 0) {
      toast.invalidForm()
      return
    }

    setSaving(true)
    service
      .saveConfig(eventId, toPayload(draft))
      .then((result) => {
        setConfig(result)
        setDraft(toDraft(result))
        toast.success(config?.survey ? 'Pesquisa NPS atualizada' : 'Pesquisa NPS criada')
        onSaved?.()
        onClose()
      })
      .catch((error) => {
        const apiErrors = (error as ApiErrorShape).response?.data?.errors ?? {}
        setErrors(
          Object.fromEntries(
            Object.entries(apiErrors).map(([key, messages]) => [key, Array.isArray(messages) ? messages : [messages]])
          )
        )
        toast.error('Erro ao salvar a pesquisa NPS', error)
      })
      .finally(() => setSaving(false))
  }

  const lockedIds = config?.locked_question_ids ?? []
  const hasSurvey = !!config?.survey

  const footer = (
    <Flex w="100%" justify="space-between" align="center" gap={3} wrap="wrap">
      <Box>
        {canViewResults && hasSurvey && (
          <Button
            as={RouterLink}
            to={`/events/${eventSlug}/pesquisa`}
            variant="ghost"
            size="sm"
            leftIcon={<Icon name="lni lni-bar-chart" />}
          >
            Ver resultados
          </Button>
        )}
      </Box>
      <FormActions
        onCancel={onClose}
        confirmType="submit"
        confirmLabel={hasSurvey ? 'Salvar' : 'Criar pesquisa'}
        isLoading={saving}
        isDisabled={!draft}
      />
    </Flex>
  )

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Pesquisa NPS do evento"
      size="editor"
      contentProps={{ maxW: '95vw', w: '1400px', h: '92vh' }}
      onSubmit={handleSave}
      noValidate
      footer={footer}
    >
      {loading ? (
        <LoadingBlock entity="pesquisa" />
      ) : loadError || !draft ? (
        <EmptyState
          tone="error"
          title="Não foi possível carregar a pesquisa"
          action={<ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={load} />}
        />
      ) : (
        <Flex h="100%" direction={{ base: 'column', md: 'row' }}>
          <Box
            flex="1"
            h="100%"
            overflowY="auto"
            borderRight={{ md: '1px solid' }}
            borderBottom={{ base: '1px solid', md: 'none' }}
            borderColor="gray.200"
          >
            <PaneHeader icon="lni lni-pencil-alt" label="Configuração" />
            <Box p={5}>
              {!hasSurvey && (
                <Alert status="info" rounded="lg" mb={4}>
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    Este evento ainda não tem pesquisa. As perguntas abaixo são o modelo padrão: ajuste o que for
                    preciso e clique em <strong>Criar pesquisa</strong>.
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                label="Pesquisa ativa"
                htmlFor="event-survey-active"
                error={errors.is_active}
                helperText={
                  <>
                    Cada participante recebe o próprio link por e-mail: no envio dos certificados, pela variável{' '}
                    <strong>#LINK_PESQUISA</strong>, ou em &quot;Enviar pesquisa&quot;. Desativada, o link mostra
                    &quot;pesquisa encerrada&quot; e nenhum dos dois envios com o link é feito.
                  </>
                }
                mb={5}
              >
                <Switch
                  id="event-survey-active"
                  isChecked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                  isDisabled={saving}
                />
              </FormField>

              {config?.survey && (
                <Text variant="caption" mb={4}>
                  {config.responses_count} {config.responses_count === 1 ? 'resposta' : 'respostas'} até agora · última
                  alteração em {formatDateTime(config.survey.updated_at)}
                </Text>
              )}

              <EventSurveyQuestionsEditor
                questions={draft.questions}
                onChange={(questions) => setDraft({ ...draft, questions })}
                errors={errors}
                lockedIds={lockedIds}
                hasResponses={!!config?.has_responses}
                disabled={saving}
              />
            </Box>
          </Box>

          <Box flex="1" h="100%" overflowY="auto" bg="#0a0a0a">
            <PaneHeader icon="lni lni-eye" label="Pré-visualização no site" dark />
            <Box p={5}>
              <EventSurveyPreview questions={draft.questions} eventName={eventName} eventImage={eventImage} />
            </Box>
          </Box>
        </Flex>
      )}
    </FormModal>
  )
}
